import { useCallback } from 'react';

import type { AdminFirmEmployeeInput } from '../../../../../../../graphql';
import {
  Rolodex_FirmDetail_FirmInvestorContactsDocument,
  useRolodex_FirmDetail_CreateFirmInvestorContactMutation,
  useRolodex_FirmDetail_DeleteFirmInvestorContactMutation,
  useRolodex_FirmDetail_FirmInvestorContactsQuery,
  useRolodex_FirmDetail_UpdateFirmInvestorContactMutation,
} from '../graphql/__generated__';

const REFETCH_OPTIONS = {
  refetchQueries: [Rolodex_FirmDetail_FirmInvestorContactsDocument],
  awaitRefetchQueries: true,
};

export function useFirmInvestorContacts(firmId: string) {
  const { data, loading, error } = useRolodex_FirmDetail_FirmInvestorContactsQuery({
    variables: { firmId },
  });

  const [createFirmEmployee, { error: createError }] =
    useRolodex_FirmDetail_CreateFirmInvestorContactMutation(REFETCH_OPTIONS);

  const [updateFirmEmployee, { error: updateError }] =
    useRolodex_FirmDetail_UpdateFirmInvestorContactMutation(REFETCH_OPTIONS);

  const [deleteFirmEmployee, { error: deleteError }] =
    useRolodex_FirmDetail_DeleteFirmInvestorContactMutation(REFETCH_OPTIONS);

  const createContact = useCallback(
    (data: AdminFirmEmployeeInput) =>
      createFirmEmployee({ variables: { input: { firmId, data } } }),
    [createFirmEmployee, firmId]
  );

  const updateContact = useCallback(
    (employeeId: string, data: AdminFirmEmployeeInput) =>
      updateFirmEmployee({ variables: { input: { firmId, employeeId, data } } }),
    [updateFirmEmployee, firmId]
  );

  const deleteContact = useCallback(
    (employeeId: string) => deleteFirmEmployee({ variables: { input: { firmId, employeeId } } }),
    [deleteFirmEmployee, firmId]
  );

  return {
    contacts: data?.firmEmployees?.items ?? [],
    totalCount: data?.firmEmployees?.totalCount ?? 0,
    loading,
    error,
    createContact,
    updateContact,
    deleteContact,
    mutationError: createError ?? updateError ?? deleteError,
  };
}
